import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getLocalizedText } from "@salon/types";
import type { TFunction } from "i18next";
import { GripVertical, LayoutTemplate, Plus, Settings, Trash2 } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import type { ControllerRenderProps } from "react-hook-form";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useServiceTemplates } from "@/domains/settings/features/service-templates";
import { useSalonStore } from "@/lib/store/salon";
import { getSalonCurrencySymbol } from "@/lib/utils/currency";
import type { ServiceOptionFormData, ServiceOptionGroupFormData } from "../../types";
import type { ServiceEditFormData } from "../../validation/service-edit-schema";
import { TemplateSelectorDialog } from "../template-selector-dialog";

// Helper function to get human-readable selection type - will be moved to component for translation
const getSelectionTypeLabel = (selectionType: string, t: TFunction<"services">) => {
  switch (selectionType) {
    case "SINGLE":
      return t("serviceMatrixOptionsForm.selectionTypes.single");
    case "MULTIPLE":
      return t("serviceMatrixOptionsForm.selectionTypes.multiple");
    default:
      return t("serviceMatrixOptionsForm.placeholders.selectType");
  }
};

interface ServiceMatrixOptionsFormProps {
  showInlineTemplates?: boolean;
}

export function ServiceMatrixOptionsForm({
  showInlineTemplates = true,
}: ServiceMatrixOptionsFormProps) {
  const { t, i18n } = useTranslation("services");
  const { control, setValue, getValues } = useFormContext<ServiceEditFormData>();
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const currency = useSalonStore((state) => state.currentSalon?.currency);
  const currencySymbol = getSalonCurrencySymbol(currency);

  // Fetch available templates for inline selection
  const { templates: allTemplates, loading: templatesLoading } = useServiceTemplates();

  const {
    fields: groupFields,
    append: appendGroup,
    remove: removeGroup,
    move: moveGroup,
  } = useFieldArray({
    control,
    name: "optionGroups",
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const pricingMode = useWatch({ control, name: "pricing.pricingMode" });
  const currentTemplateId = useWatch({ control, name: "templateId" as keyof ServiceEditFormData });

  // Only show if pricing mode is MATRIX_PRICE
  if (pricingMode !== "MATRIX_PRICE") {
    return null;
  }

  const resolveText = (value: unknown): string => {
    if (typeof value === "string") return value;
    if (value && typeof value === "object") {
      return getLocalizedText(value as Record<string, string>, i18n.language) ?? "";
    }
    return "";
  };

  // Apply a template: replace all groups (client-side) and store templateId reference
  const handleSelectTemplate = (templateId: string) => {
    if (currentTemplateId === templateId) {
      // Deselect: clear templateId but keep groups as-is (user can continue editing)
      setValue("templateId", null);
      return;
    }

    const template = allTemplates.find((tpl) => tpl.id === templateId);
    if (!template) return;

    const newGroups = template.groups.map((group, groupIdx) => ({
      name: resolveText(group.name),
      selectionType: group.selectionType as "SINGLE" | "MULTIPLE",
      minSelections: group.minSelections,
      maxSelections: group.maxSelections ?? null,
      sortOrder: groupIdx,
      options: group.options.map((opt, optIdx) => ({
        optionName: resolveText(opt.optionName),
        priceModifier: 0,
        durationModifier: opt.durationModifier,
        isDefault: opt.isDefault,
        sortOrder: optIdx,
      })),
    }));

    setValue("optionGroups", newGroups as ServiceEditFormData["optionGroups"]);
    setValue("templateId", templateId);
  };

  const handleAddGroup = () => {
    // Manual addition clears template reference
    setValue("templateId" as keyof ServiceEditFormData, null as never, { shouldValidate: false });
    appendGroup(
      {
        name: t("serviceMatrixOptionsForm.defaults.newGroupName"),
        selectionType: "SINGLE",
        minSelections: 0,
        maxSelections: null,
        sortOrder: groupFields.length,
        options: [],
      },
      { shouldFocus: false }
    );
  };

  const handleRemoveGroup = (groupIndex: number) => {
    // Manual removal clears template reference
    setValue("templateId" as keyof ServiceEditFormData, null as never, { shouldValidate: false });
    removeGroup(groupIndex);
  };

  // Resolve selected template name for display
  const selectedTemplate = currentTemplateId
    ? allTemplates.find((tpl) => tpl.id === currentTemplateId)
    : null;
  const selectedTemplateName = selectedTemplate ? resolveText(selectedTemplate.name) : null;

  // Group editing is now inline, no need for separate handler

  // Option adding is handled by each OptionGroupCard using its own appendOption method
  // This ensures proper form state management and dirty tracking

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = groupFields.findIndex((field) => field.id === active.id);
      const newIndex = groupFields.findIndex((field) => field.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        moveGroup(oldIndex, newIndex);

        // Reorder clears template reference
        setValue("templateId", null);

        // Update sortOrder values
        const currentGroups = getValues("optionGroups");
        const reorderedGroups = arrayMove(currentGroups, oldIndex, newIndex);
        reorderedGroups.forEach((_group, index) => {
          setValue(`optionGroups.${index}.sortOrder`, index);
        });
      }
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle>{t("serviceMatrixOptionsForm.title")}</CardTitle>
              {/* Template / Custom status badge */}
              {selectedTemplateName ? (
                <Badge variant="secondary" className="text-xs font-normal">
                  {selectedTemplateName}
                </Badge>
              ) : groupFields.length > 0 ? (
                <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                  {t("serviceMatrixOptionsForm.customLabel", "Custom")}
                </Badge>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              {!showInlineTemplates && (
                <Button variant="outline" size="sm" onClick={() => setTemplateDialogOpen(true)}>
                  <LayoutTemplate className="w-4 h-4 mr-2" />
                  {t("serviceMatrixOptionsForm.actions.useTemplate", "Use Template")}
                </Button>
              )}
              <Button onClick={handleAddGroup} size="default">
                <Plus className="w-4 h-4 mr-2" />
                {t("serviceMatrixOptionsForm.actions.addGroup")}
              </Button>
            </div>
          </div>

          {/* Inline template selection - only in create dialog */}
          {showInlineTemplates && (allTemplates.length > 0 || templatesLoading) && (
            <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3 space-y-2 dark:border-rose-900/60 dark:bg-rose-950/30">
              <div className="flex items-center gap-2">
                <LayoutTemplate className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                <p className="text-sm font-semibold text-rose-700 dark:text-rose-300">
                  {t("serviceMatrixOptionsForm.templateHint", "Quick start with a template:")}
                </p>
              </div>
              {templatesLoading ? (
                <div className="flex flex-wrap gap-1.5">
                  <div className="h-6 w-24 rounded-full bg-muted animate-pulse" />
                  <div className="h-6 w-32 rounded-full bg-muted animate-pulse" />
                  <div className="h-6 w-20 rounded-full bg-muted animate-pulse" />
                </div>
              ) : (
                <div className="space-y-2">
                  {[
                    {
                      scope: "SYSTEM",
                      label: t("serviceMatrixOptionsForm.templateGroups.system", "System"),
                      items: allTemplates.filter((tpl) => tpl.scope === "SYSTEM"),
                    },
                    {
                      scope: "FRANCHISE",
                      label: t("serviceMatrixOptionsForm.templateGroups.franchise", "Custom"),
                      items: allTemplates.filter((tpl) => tpl.scope === "FRANCHISE"),
                    },
                  ]
                    .filter((group) => group.items.length > 0)
                    .map((group) => (
                      <div key={group.scope} className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-muted-foreground shrink-0">
                          {group.label}
                        </span>
                        {group.items.map((template) => {
                          const name = resolveText(template.name);
                          const isSelected = currentTemplateId === template.id;
                          return (
                            <button
                              key={template.id}
                              type="button"
                              onClick={() => handleSelectTemplate(template.id)}
                              className={[
                                "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-all focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                                isSelected
                                  ? "bg-primary text-primary-foreground border-primary shadow-sm"
                                  : "bg-background text-foreground border-border hover:bg-muted",
                              ].join(" ")}
                            >
                              {name}
                              {isSelected && (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="10"
                                  height="10"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="3"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="opacity-80"
                                  aria-hidden="true"
                                >
                                  <title>Selected</title>
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
          {/* Booking page display mode */}
          <FormField
            control={control}
            name="basicInfo.storefrontDisplayMode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("serviceInformationForm.fields.storefrontDisplayMode", "Booking page display")}
                </FormLabel>
                <FormDescription>
                  {t(
                    "serviceInformationForm.descriptions.storefrontDisplayMode",
                    "Overrides the template setting. Leave as 'Inherit' to use the template default."
                  )}
                </FormDescription>
                <Select
                  onValueChange={(v) => field.onChange(v === "inherit" ? null : v)}
                  value={field.value ?? "inherit"}
                >
                  <FormControl>
                    <SelectTrigger className="w-56">
                      <SelectValue>
                        {field.value === "TABLE"
                          ? t("serviceInformationForm.displayMode.table", "Price table")
                          : field.value === "REGULAR"
                            ? t("serviceInformationForm.displayMode.regular", "Regular row")
                            : t(
                                "serviceInformationForm.displayMode.inherit",
                                "Inherit from template"
                              )}
                      </SelectValue>
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="inherit">
                      {t("serviceInformationForm.displayMode.inherit", "Inherit from template")}
                    </SelectItem>
                    <SelectItem value="TABLE">
                      {t("serviceInformationForm.displayMode.table", "Price table")}
                    </SelectItem>
                    <SelectItem value="REGULAR">
                      {t("serviceInformationForm.displayMode.regular", "Regular row")}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardHeader>

        <CardContent>
          {groupFields.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={groupFields.map((field) => field.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-6">
                  {groupFields.map((groupField, groupIndex) => {
                    const group = groupField as ServiceOptionGroupFormData;
                    return (
                      <SortableOptionGroupCard
                        key={groupField.id}
                        id={groupField.id}
                        group={group}
                        groupIndex={groupIndex}
                        onRemoveGroup={() => handleRemoveGroup(groupIndex)}
                        currencySymbol={currencySymbol}
                        // Option adding is handled internally by OptionGroupCard
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <div className="text-center py-4 text-muted-foreground">
              <div className="rounded-lg border-2 border-dashed border-gray-200 p-4">
                <Settings className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <h3 className="font-medium mb-2">
                  {t("serviceMatrixOptionsForm.emptyState.title")}
                </h3>
                <p className="text-sm mb-6 text-muted-foreground max-w-md mx-auto">
                  {t("serviceMatrixOptionsForm.emptyState.description")}
                </p>
                <Button onClick={handleAddGroup}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t("serviceMatrixOptionsForm.actions.createFirstGroup")}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {!showInlineTemplates && (
        <TemplateSelectorDialog
          open={templateDialogOpen}
          onOpenChange={setTemplateDialogOpen}
          onSelectTemplate={(templateId) => {
            handleSelectTemplate(templateId);
            setTemplateDialogOpen(false);
          }}
        />
      )}
    </>
  );
}

interface OptionGroupCardProps {
  group: ServiceOptionGroupFormData;
  groupIndex: number;
  onRemoveGroup: () => void;
  dragHandleProps?: React.HTMLAttributes<SVGElement>;
  currencySymbol: string;
}

interface SortableOptionGroupCardProps extends OptionGroupCardProps {
  id: string;
}

function SortableOptionGroupCard({ id, ...props }: SortableOptionGroupCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <OptionGroupCard {...props} dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  );
}

function OptionGroupCard({
  groupIndex,
  onRemoveGroup,
  dragHandleProps,
  currencySymbol,
}: OptionGroupCardProps) {
  const { t } = useTranslation("services");
  const { control, setValue, getValues } = useFormContext<ServiceEditFormData>();

  const {
    fields: optionFields,
    append: appendOption,
    remove: removeOption,
    move: moveOption,
  } = useFieldArray({
    control,
    name: `optionGroups.${groupIndex}.options`,
  });

  const optionSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const clearTemplate = () => {
    setValue("templateId", null, { shouldValidate: false });
  };

  // Handle adding options using useFieldArray append method for proper form state management
  const handleAddOption = () => {
    clearTemplate();
    appendOption(
      {
        optionName: t("serviceMatrixOptionsForm.defaults.newOptionName"),
        priceModifier: 0,
        durationModifier: 0,
        isDefault: false,
        sortOrder: optionFields.length,
      },
      { shouldFocus: false }
    );
  };

  const handleRemoveOption = (optionIndex: number) => {
    clearTemplate();
    removeOption(optionIndex);
  };

  const handleOptionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = optionFields.findIndex((field) => field.id === active.id);
      const newIndex = optionFields.findIndex((field) => field.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        clearTemplate();
        moveOption(oldIndex, newIndex);

        // Update sortOrder values for options
        const currentOptions = getValues(`optionGroups.${groupIndex}.options`);
        const reorderedOptions = arrayMove(currentOptions, oldIndex, newIndex);
        reorderedOptions.forEach((_option, index) => {
          setValue(`optionGroups.${groupIndex}.options.${index}.sortOrder`, index);
        });
      }
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Group Header with Inline Editing - Responsive Layout */}
      <div className="bg-muted px-4 py-3 border-b">
        <div className="space-y-3">
          {/* Top Row: Drag Handle + Group Name + Actions */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <GripVertical
                className="w-4 h-4 text-muted-foreground cursor-grab flex-shrink-0 hover:text-foreground"
                {...dragHandleProps}
              />

              {/* Group Name - Full width on mobile */}
              <div className="flex-1 min-w-0">
                <Label htmlFor={`group-${groupIndex}-name`} className="sr-only">
                  {t("serviceMatrixOptionsForm.fields.groupName")}
                </Label>
                <Input
                  id={`group-${groupIndex}-name`}
                  placeholder={t("serviceMatrixOptionsForm.placeholders.groupName")}
                  className="h-8 text-sm font-medium bg-background"
                  {...control.register(`optionGroups.${groupIndex}.name`)}
                />
              </div>
            </div>

            {/* Action Buttons - Always visible on the right */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddOption}
                className="h-8 hidden sm:flex"
              >
                <Plus className="w-4 h-4 mr-1" />
                {t("serviceMatrixOptionsForm.actions.addOption")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddOption}
                className="h-8 sm:hidden"
                title="Add Option"
              >
                <Plus className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onRemoveGroup}
                className="h-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                title="Delete Group"
              >
                <Trash2 className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">
                  {t("serviceMatrixOptionsForm.actions.delete")}
                </span>
              </Button>
            </div>
          </div>

          {/* Bottom Row: Selection Type + Required Toggle - Responsive */}
          <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
            {/* Selection Type */}
            <div className="flex-1 min-w-[140px]">
              <FormField
                control={control}
                name={`optionGroups.${groupIndex}.selectionType`}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger className="h-8 text-sm bg-background">
                          <SelectValue
                            placeholder={t("serviceMatrixOptionsForm.placeholders.selectType")}
                          >
                            {getSelectionTypeLabel(field.value, t)}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="SINGLE">
                            {t("serviceMatrixOptionsForm.selectionTypes.single")}
                          </SelectItem>
                          <SelectItem value="MULTIPLE">
                            {t("serviceMatrixOptionsForm.selectionTypes.multiple")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <p className="text-xs text-muted-foreground mt-1">
                      {field.value === "MULTIPLE"
                        ? t("serviceMatrixOptionsForm.selectionTypeHints.multiple")
                        : t("serviceMatrixOptionsForm.selectionTypeHints.single")}
                    </p>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Options List */}
      {optionFields.length > 0 ? (
        <DndContext
          sensors={optionSensors}
          collisionDetection={closestCenter}
          onDragEnd={handleOptionDragEnd}
        >
          <SortableContext
            items={optionFields.map((field) => field.id)}
            strategy={verticalListSortingStrategy}
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="border-t border-b bg-muted/40">
                  <th className="w-7" />
                  <th className="py-2 px-3 text-left text-xs font-medium text-muted-foreground">
                    {t("serviceMatrixOptionsForm.fields.optionName")}
                  </th>
                  <th className="py-2 px-3 text-left text-xs font-medium text-muted-foreground w-36">
                    {t("serviceMatrixOptionsForm.fields.priceModifier")}
                  </th>
                  <th className="w-9" />
                </tr>
              </thead>
              <tbody>
                {optionFields.map((optionField, optionIndex) => {
                  const option = optionField as ServiceOptionFormData;
                  return (
                    <SortableOptionItem
                      key={optionField.id}
                      id={optionField.id}
                      option={option}
                      optionIndex={optionIndex}
                      groupIndex={groupIndex}
                      onRemoveOption={() => handleRemoveOption(optionIndex)}
                      currencySymbol={currencySymbol}
                    />
                  );
                })}
              </tbody>
            </table>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="p-4 text-center text-muted-foreground">
          <div className="rounded-lg border-2 border-dashed border-gray-200 p-6">
            <Settings className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm mb-3">{t("serviceMatrixOptionsForm.emptyGroup.message")}</p>
            <Button variant="outline" size="default" onClick={handleAddOption}>
              <Plus className="w-4 h-4 mr-2" />
              {t("serviceMatrixOptionsForm.actions.addFirstOption")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Extracted component so useRef/useEffect hooks can be at the top level
function PriceModifierInput({
  field,
  groupIndex,
  optionIndex,
  currencySymbol,
}: {
  field: ControllerRenderProps<
    ServiceEditFormData,
    `optionGroups.${number}.options.${number}.priceModifier`
  >;
  groupIndex: number;
  optionIndex: number;
  currencySymbol: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current && field.value !== undefined) {
      const displayValue = field.value !== 0 ? (field.value / 100).toFixed(2) : "";
      if (inputRef.current.value !== displayValue) {
        inputRef.current.value = displayValue;
      }
    }
  }, [field.value]);

  return (
    <FormItem>
      <FormControl>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none z-10">
            <span className="text-muted-foreground text-xs font-medium">{currencySymbol}</span>
          </div>
          <Input
            ref={inputRef}
            id={`option-${groupIndex}-${optionIndex}-price`}
            type="text"
            placeholder="0.00"
            className="h-8 text-sm pl-6 text-right"
            defaultValue={field.value !== 0 ? (field.value / 100).toFixed(2) : ""}
            onBlur={(e) => {
              const value = e.target.value.trim();
              if (value === "" || value === "0" || value === "0.00" || value === "-") {
                field.onChange(0);
                e.target.value = "";
              } else {
                const dollars = Number.parseFloat(value);
                if (!Number.isNaN(dollars) && dollars >= -999.99 && dollars <= 999.99) {
                  field.onChange(Math.round(dollars * 100));
                  e.target.value = dollars.toFixed(2);
                } else {
                  const previousValue = field.value !== 0 ? (field.value / 100).toFixed(2) : "";
                  e.target.value = previousValue;
                }
              }
            }}
            onKeyDown={(e) => {
              if (
                [8, 9, 27, 13, 46, 110, 190, 109, 189].indexOf(e.keyCode) !== -1 ||
                (e.keyCode === 65 && e.ctrlKey === true) ||
                (e.keyCode === 67 && e.ctrlKey === true) ||
                (e.keyCode === 86 && e.ctrlKey === true) ||
                (e.keyCode === 88 && e.ctrlKey === true)
              ) {
                return;
              }
              if (
                (e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
                (e.keyCode < 96 || e.keyCode > 105)
              ) {
                e.preventDefault();
              }
            }}
          />
        </div>
      </FormControl>
    </FormItem>
  );
}

interface SortableOptionItemProps {
  id: string;
  option: ServiceOptionFormData;
  optionIndex: number;
  groupIndex: number;
  onRemoveOption: () => void;
  currencySymbol: string;
}

function SortableOptionItem({
  id,
  optionIndex,
  groupIndex,
  onRemoveOption,
  currencySymbol,
}: SortableOptionItemProps) {
  const { t } = useTranslation("services");
  const { control } = useFormContext<ServiceEditFormData>();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="border-b last:border-0 group hover:bg-muted/30 transition-colors"
    >
      {/* Drag handle */}
      <td className="pl-2 w-7">
        <GripVertical
          className="w-4 h-4 text-muted-foreground/40 cursor-grab hover:text-muted-foreground group-hover:text-muted-foreground/70 transition-colors"
          {...attributes}
          {...listeners}
        />
      </td>

      {/* Option Name */}
      <td className="py-1.5 px-2">
        <Input
          id={`option-${groupIndex}-${optionIndex}-name`}
          className="h-8 text-sm"
          placeholder={t("serviceMatrixOptionsForm.placeholders.optionName")}
          {...control.register(`optionGroups.${groupIndex}.options.${optionIndex}.optionName`)}
        />
      </td>

      {/* Price Modifier */}
      <td className="py-1.5 px-2 w-36">
        <FormField
          control={control}
          name={`optionGroups.${groupIndex}.options.${optionIndex}.priceModifier`}
          render={({ field }) => (
            <PriceModifierInput
              field={field}
              groupIndex={groupIndex}
              optionIndex={optionIndex}
              currencySymbol={currencySymbol}
            />
          )}
        />
      </td>

      {/* Delete */}
      <td className="py-1.5 pr-2 w-9">
        <Button
          variant="ghost"
          size="icon"
          onClick={onRemoveOption}
          className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </td>

      {/* Hidden fields for form registration */}
      <td className="hidden" aria-hidden="true">
        <input
          type="number"
          {...control.register(
            `optionGroups.${groupIndex}.options.${optionIndex}.durationModifier`,
            { valueAsNumber: true }
          )}
        />
        <FormField
          control={control}
          name={`optionGroups.${groupIndex}.options.${optionIndex}.isDefault`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
            </FormItem>
          )}
        />
      </td>
    </tr>
  );
}
